import { useEffect, useState } from "react";

/**
 * Whether the element has scrolled up past the line `topOffset` px below the viewport top. An element
 * still below the fold has not scrolled past, although it does not intersect either.
 *
 * @returns `observe`, a callback ref, so the element may mount, change or unmount at any time;
 * `scrolledPast`, which is false while no element is attached.
 */
export function useScrolledPast<T extends Element>(topOffset: number) {
  const [element, setElement] = useState<T | null>(null);
  // Keyed by element, so a replaced or removed element never inherits the previous one's answer.
  const [pastElement, setPastElement] = useState<T | null>(null);

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const lineTop = entry.rootBounds?.top ?? topOffset;
        const isPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= lineTop;
        setPastElement(isPast ? element : null);
      },
      { rootMargin: `-${topOffset}px 0px 0px 0px` },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, topOffset]);

  return { observe: setElement, scrolledPast: element !== null && pastElement === element };
}
